import '@pinflag/pinflag-ui-kit/styles'
import React, { Suspense, useContext, useEffect, useState } from 'react'
import { CheckoutSkeleton } from '@pinflag/pinflag-ui-kit'
import { DataContext } from './context/DataContext'
import { getCompanyInfoHelper } from './helpers/getCompanyInfoHelper'
import RedirectLoader from './components/RedirectLoader'
import { resolveMapboxToken } from './utils/encrypt'
import { lazyWithRetry } from './helpers/retryImport'
import { bootstrapSession } from './helpers/session'
import { getPinflagIdentity } from './helpers/utils'

const PinMapProLazy = lazyWithRetry(() => import('./PinmapPro'))
const MagentoProviderLazy = lazyWithRetry(() => import('./context/MagentoContext').then(module => ({ default: module.MagentoProvider })))
const PaymentProviderLazy = lazyWithRetry(() => import('./context/PaymentContext').then(module => ({ default: module.PaymentProvider })))

const App = () => {
  const { setUserInfo, setCompanyInfo, setIsSaving, setAbandonedCartData, isPickup, companyInfo } = useContext(DataContext)
  const isLoaderIframe = new URLSearchParams(document.location.search).get('loaderIframe')
  const paymentRedirect = new URLSearchParams(document.location.search).get('paymentRedirect')
  const isLoaderOrRedirect = paymentRedirect || isLoaderIframe
  const [app, setApp] = useState(isLoaderOrRedirect ? <RedirectLoader /> : <CheckoutSkeleton isPickup={isPickup} />)
  const isAbandonedCart = new URLSearchParams(document.location.search).get('abandonedCart')

  useEffect(() => {
    window.addEventListener('message', receiveMessage)

    bootstrapSession()
      .catch(() => null)
      .finally(() =>
        getCompanyInfoHelper(setUserInfo, setCompanyInfo, setIsSaving, setAbandonedCartData, isAbandonedCart)
      )

    return () => {
      window.removeEventListener('message', receiveMessage)
    }
  }, [])

  useEffect(() => {
    if (isLoaderOrRedirect) {
      setApp(
        <Suspense fallback={<RedirectLoader />}>
          <MagentoProviderLazy>
            <PaymentProviderLazy>
              <PinMapProLazy/>
            </PaymentProviderLazy>
          </MagentoProviderLazy>
        </Suspense>
      )
      window.removeEventListener('message', receiveMessage)
    }
  }, [paymentRedirect, isLoaderIframe])

  const processCompanyInfo = (companyData) => {
    const processedCompanyData = {
      ...companyData,
      mapboxToken: resolveMapboxToken(companyData.mapboxToken),
      customBillingNames: companyData.customBillingNames ??
        companyData.cmsBillingFieldsCustomNames?.fields,
      billingDocName: companyData.billingDocName ??
        companyData.cmsBillingFieldsCustomNames?.docName,
      activeBillingFields: companyData.activeBillingFields ??
        companyData.cmsActiveBillingFields
    }

    setCompanyInfo((prevState) => ({
      ...prevState,
      ...processedCompanyData
    }))
    if (companyData.useOneClick !== undefined) setIsSaving(companyData.useOneClick > 0)
  }

  const receiveMessage = async (event) => {
    if (event?.data?.type) {
      const { message, origin, type, pinflagApiKey } = event.data

      if (type === 'request-company-info') {
        const currentPinflagApiKey = getPinflagIdentity()
        if (
          event.source === window.parent &&
          pinflagApiKey === currentPinflagApiKey &&
          companyInfo?.companyName
        ) {
          window.parent.postMessage(
            {
              type: 'response-company-info',
              origin: 'pinflag-shopify-pinmap-pro',
              pinflagApiKey: currentPinflagApiKey,
              message: companyInfo
            },
            '*'
          )
        }
      } else if (type === 'receive-company-info') {
        processCompanyInfo(message)
      } else if (type === 'receive-cart-info') {
        if (message?.companyInfo) {
          processCompanyInfo(message.companyInfo)
        } else {
          getCompanyInfoHelper(setUserInfo, setCompanyInfo, setIsSaving, setAbandonedCartData, isAbandonedCart)
        }
        setApp(
          <Suspense fallback={<CheckoutSkeleton isPickup={isPickup} />}>
            <MagentoProviderLazy>
              <PaymentProviderLazy>
                <PinMapProLazy cartMessage={message} cartOrigin={origin}/>
              </PaymentProviderLazy>
            </MagentoProviderLazy>
          </Suspense>
        )
        window.removeEventListener('message', receiveMessage)
      } else if (type === 'is-ready') {
        if (!companyInfo?.companyName) {
          getCompanyInfoHelper(setUserInfo, setCompanyInfo, setIsSaving, setAbandonedCartData, isAbandonedCart)
        }
      }
    }
  }

  return app
}

export default App
